/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { NotificationPrefsSchema, type NotificationPrefsInput } from '@therapy/shared-validators';
import { type SyntheticEvent, useCallback, type JSX } from 'react';
import { type UseFormRegister, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { type NotificationPrefs } from '../../lib/api/clinic.js';
import { useUpdateNotificationPrefs } from '../../lib/queries/clinic.js';

interface NotificationsFormProps {
  readonly prefs: NotificationPrefs;
}

const PREF_FIELDS = [
  { key: 'sessionReminders', label: 'settings.notifications.sessionReminders' },
  { key: 'paymentAlerts', label: 'settings.notifications.paymentAlerts' },
  { key: 'progressReports', label: 'settings.notifications.progressReports' },
  { key: 'systemUpdates', label: 'settings.notifications.systemUpdates' },
] as const;

interface PrefRowProps {
  readonly fieldKey: keyof NotificationPrefs;
  readonly label: string;
  readonly register: UseFormRegister<NotificationPrefsInput>;
}

function PrefRow({ fieldKey, label, register }: PrefRowProps): JSX.Element {
  const { t } = useTranslation();
  const id = `notif-${fieldKey}`;
  return (
    <label
      htmlFor={id}
      className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 px-4 py-3 dark:border-slate-700"
    >
      <span className="space-y-0.5">
        <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">
          {t(label)}
        </span>
        <span className="block text-xs text-slate-500 dark:text-slate-400">
          {t(`${label}Desc`)}
        </span>
      </span>
      <input
        id={id}
        type="checkbox"
        className="focus-ring text-brand-600 mt-1 h-4 w-4 rounded border-slate-300"
        {...register(fieldKey)}
      />
    </label>
  );
}

/** Clinic Admin → Settings → Notifications. Validated by `NotificationPrefsSchema`. */
export function NotificationsForm({ prefs }: NotificationsFormProps): JSX.Element {
  const { t } = useTranslation();
  const update = useUpdateNotificationPrefs();
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<NotificationPrefsInput>({
    resolver: zodResolver(NotificationPrefsSchema),
    defaultValues: prefs,
  });

  const submit = useCallback(
    async (values: NotificationPrefsInput): Promise<void> => {
      await update.mutateAsync(values);
    },
    [update],
  );

  const onFormSubmit = useCallback(
    (event: SyntheticEvent<HTMLFormElement>): void => {
      void handleSubmit(submit)(event);
    },
    [handleSubmit, submit],
  );

  return (
    <form onSubmit={onFormSubmit} className="max-w-2xl space-y-4" noValidate>
      <div className="space-y-3">
        {PREF_FIELDS.map((field) => (
          <PrefRow key={field.key} fieldKey={field.key} label={field.label} register={register} />
        ))}
      </div>

      {update.isError && (
        <p role="alert" className="text-sm text-red-600">
          {t('common.saveError')}
        </p>
      )}
      {update.isSuccess && (
        <p role="status" className="text-sm text-emerald-600">
          {t('settings.saved')}
        </p>
      )}

      <Button type="submit" disabled={isSubmitting || update.isPending}>
        {t('settings.save')}
      </Button>
    </form>
  );
}
